import React, { useState } from 'react';
import { CheckCircle, ArrowLeft, DollarSign, AlertCircle } from 'lucide-react';
import apiService from '../../apiService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function submitPayuForm(formParams) {
  const { action, ...fields } = formParams || {};

  if (!action) {
    throw new Error('Invalid PayU payload: missing action URL');
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value ?? '';
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

// ---------------------------------------------------------------------------
// PaymentModal
// ---------------------------------------------------------------------------

const PaymentModal = ({
  isOpen,
  onClose,
  paymentMethod,   // 'cod' | 'upi' | 'card' | 'wallet' | 'netbanking'
  totalAmount,
  orderId,         // required for PayU (order placed before payment)
  userPhone,       // optional, passed to PayU
  onPaymentComplete,
  payuEnabled = true,  // Whether PayU online payments are enabled
  codEnabled  = true,  // Whether Cash on Delivery is enabled
}) => {
  const [step, setStep]               = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]       = useState('');
  const [processing, setProcessing]   = useState(false);


  // Handler for COD/UPI Scan on Delivery
  const handleCOD = async () => {
    try {
      setProcessing(true);
      // Simulate API call or order confirmation
      // You can replace this with your actual API logic
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onPaymentComplete) onPaymentComplete();
    } catch (err) {
      setErrorMsg('Failed to confirm order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for PayU payment
  const handlePayU = async () => {
    try {
      setProcessing(true);
      // Simulate PayU payment flow
      // Replace with actual PayU integration as needed
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onPaymentComplete) onPaymentComplete();
    } catch (err) {
      setErrorMsg('Failed to initiate PayU payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Payment method labels and icons
  const methodLabels = {
    upi:        { label: 'UPI Payment',   icon: '📱', color: 'from-purple-500 to-blue-600'    },
    card:       { label: 'Card Payment',  icon: '💳', color: 'from-gray-700 to-gray-900'       },
    wallet:     { label: 'Wallet Payment',icon: '👛', color: 'from-blue-500 to-cyan-500'       },
    netbanking: { label: 'Net Banking',   icon: '🏦', color: 'from-green-600 to-emerald-700'  },
  };

  // ── COD or UPI Scan on Delivery Screen ──
  if (paymentMethod === 'cod' || paymentMethod === 'upi_cod') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-xl font-bold">{paymentMethod === 'upi_cod' ? 'UPI Scan on Delivery' : 'Cash on Delivery'}</h2>
            <div className="w-10" />
          </div>
          <div className="text-center mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {paymentMethod === 'upi_cod' ? (
                <span role="img" aria-label="UPI">📱</span>
              ) : (
                <DollarSign size={48} className="text-green-600" />
              )}
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {paymentMethod === 'upi_cod' ? 'Pay via UPI QR at Delivery' : 'Pay When You Receive'}
            </h3>
            <p className="text-gray-600 text-sm">
              {paymentMethod === 'upi_cod'
                ? 'Scan the UPI QR code at delivery to pay digitally. No cash required.'
                : 'Keep exact change ready for hassle-free delivery'}
            </p>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{paymentMethod === 'upi_cod' ? '📱' : '💵'}</span>
              <div>
                <p className="font-semibold text-yellow-800 mb-1">Amount to be Collected</p>
                <p className="text-3xl font-bold text-yellow-900">₹{totalAmount}</p>
              </div>
            </div>
          </div>
          {paymentMethod === 'upi_cod' && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 flex flex-col items-center">
              <iframe
                src="/static/upi-qr.html"
                title="UPI QR Code"
                style={{ border: 'none', width: 300, height: 300, margin: '0 auto', background: 'transparent' }}
                aria-label="UPI QR Code"
              />
              <p className="text-xs text-gray-500 mt-2">Show this QR to the delivery agent to pay</p>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
            {(paymentMethod === 'upi_cod'
              ? [
                  'No online payment required in advance',
                  'Pay securely via UPI at your doorstep',
                  'Safe and contactless delivery',
                ]
              : [
                  'No online payment required',
                  'Pay only after receiving your order',
                  'Safe and secure delivery',
                ]
            ).map((txt) => (
              <div key={txt} className="flex items-center gap-3">
                <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{txt}</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleCOD}
            disabled={processing}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              processing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : paymentMethod === 'upi_cod'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-purple-600 hover:to-purple-800'
                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
            }`}
          >
            {processing
              ? <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Confirming…
                </span>
              : paymentMethod === 'upi_cod' ? 'Confirm UPI on Delivery Order' : 'Confirm COD Order'
            }
          </button>
        </div>
      </div>
    );
  }

  // ── Online Payment (UPI / Card / Wallet / Netbanking) via PayU ──────────
  const current = methodLabels[paymentMethod] || { label: 'Online Payment', icon: '🔒', color: 'from-blue-500 to-blue-700' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-xl font-bold">{current.label}</h2>
          <div className="w-10" />
        </div>
        {/* Amount banner */}
        <div className={`bg-gradient-to-br ${current.color} rounded-2xl p-6 mb-6 text-white text-center`}>
          <div className="text-4xl mb-2">{current.icon}</div>
          <div className="text-sm opacity-90 mb-1">Amount to Pay</div>
          <div className="text-4xl font-bold">₹{totalAmount}</div>
        </div>
        {/* PayU info */}
        <div className="bg-blue-50 rounded-2xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/PayU.svg/200px-PayU.svg.png"
              alt="PayU"
              className="w-16 h-auto mt-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <p className="font-semibold text-gray-800 text-sm">Powered by PayU</p>
              <p className="text-xs text-gray-500 mt-1">
                You will be redirected to PayU's secure checkout. After payment,
                you will return here automatically.
              </p>
            </div>
          </div>
        </div>
        {/* Accepted methods */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['Visa', 'Mastercard', 'UPI', 'Paytm', 'PhonePe', 'Net Banking'].map((m) => (
            <span key={m} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{m}</span>
          ))}
        </div>
        {/* Security */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
          <span>🔒</span>
          <span>256-bit SSL encryption · PCI-DSS compliant · 3D Secure</span>
        </div>
        {/* Pay button */}
        <button
          onClick={handlePayU}
          disabled={processing}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
            processing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
          }`}
        >
          {processing
            ? <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Connecting to PayU…
              </span>
            : `Pay ₹${totalAmount} via PayU`
          }
        </button>
        <p className="text-center text-xs text-gray-400 mt-3">
          A secure PayU page will open for payment and redirect you back after completion.
        </p>
      </div>
    </div>
  );
};
export default PaymentModal;
